/**
 * Problem Confirmation Modal (DC-732)
 *
 * Modal that appears after file upload showing AI-inferred problem
 * characteristics for user confirmation.
 *
 * Features:
 * - Display inferred problem type, target column, domain, protected attributes
 * - Show confidence levels with visual indicators
 * - Allow user to confirm, edit, or reject each inference
 * - Submit confirmed declaration to API
 */

import React, { useState, useEffect } from 'react';
import './contextual-validity.css';

// ============================================================================
// TYPES
// ============================================================================

interface InferredValue {
  value: string;
  confidence: number;
  reasoning: string;
}

interface InferredProtectedAttribute {
  column: string;
  attribute_type: string;
  confidence: number;
  reasoning: string;
}

interface FeatureRole {
  column: string;
  role: string;
  confidence: number;
}

interface ProblemInference {
  inferred_problem_type: InferredValue;
  inferred_target_column?: InferredValue;
  inferred_domain?: InferredValue;
  inferred_protected_attributes: InferredProtectedAttribute[];
  feature_roles: FeatureRole[];
  overall_confidence: number;
  requires_user_confirmation: boolean;
}

interface SuggestedDeclaration {
  journey_id: string;
  problem_type: string;
  problem_statement: string;
  target_column?: string;
  domain: string;
  sensitivity_level: string;
  protected_attributes: string[];
}

export interface ProblemConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (declaration: SuggestedDeclaration) => void;
  journeyId: string;
  inference: ProblemInference;
  suggestedDeclaration: SuggestedDeclaration;
}

// ============================================================================
// HELPER: Confidence indicator
// ============================================================================

const getConfidenceLevel = (confidence: number): { label: string; className: string } => {
  if (confidence >= 0.8) return { label: 'High', className: 'confidence-high' };
  if (confidence >= 0.6) return { label: 'Medium', className: 'confidence-medium' };
  if (confidence >= 0.4) return { label: 'Low', className: 'confidence-low' };
  return { label: 'Very Low', className: 'confidence-very-low' };
};

// ============================================================================
// OPTIONS (matches ProblemDeclarationForm)
// ============================================================================

const PROBLEM_TYPES = [
  { value: 'binary_classification', label: 'Binary Classification' },
  { value: 'multiclass_classification', label: 'Multi-class Classification' },
  { value: 'regression', label: 'Regression' },
  { value: 'clustering', label: 'Clustering' },
  { value: 'time_series', label: 'Time Series' },
  { value: 'ranking', label: 'Ranking' },
  { value: 'recommendation', label: 'Recommendation' },
  { value: 'anomaly_detection', label: 'Anomaly Detection' },
  { value: 'nlp', label: 'NLP / Text Processing' },
  { value: 'other', label: 'Other' },
];

const DOMAINS = [
  { value: 'healthcare', label: 'Healthcare / Life Sciences' },
  { value: 'pharma', label: 'Pharmaceuticals' },
  { value: 'biotech', label: 'Biotechnology' },
  { value: 'finance', label: 'Finance / Banking' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'retail', label: 'Retail / E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'government', label: 'Government / Public Sector' },
  { value: 'education', label: 'Education' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'legal', label: 'Legal' },
  { value: 'other', label: 'Other' },
];

const SENSITIVITY_LEVELS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ProblemConfirmationModal: React.FC<ProblemConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  journeyId,
  inference,
  suggestedDeclaration,
}) => {
  // Form state (editable version of suggested declaration)
  const [formValues, setFormValues] = useState<SuggestedDeclaration>(suggestedDeclaration);
  const [problemStatement, setProblemStatement] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track which fields user has manually edited
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen && suggestedDeclaration) {
      setFormValues(suggestedDeclaration);
      setProblemStatement(suggestedDeclaration.problem_statement || '');
      setEditedFields(new Set());
      setError(null);
    }
  }, [isOpen, suggestedDeclaration]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFieldChange = (field: keyof SuggestedDeclaration, value: string | string[]) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setEditedFields(prev => new Set([...prev, field]));
  };

  const handleProtectedAttributeToggle = (attr: string) => {
    setFormValues(prev => {
      const current = prev.protected_attributes || [];
      const updated = current.includes(attr)
        ? current.filter(a => a !== attr)
        : [...current, attr];
      return { ...prev, protected_attributes: updated };
    });
    setEditedFields(prev => new Set([...prev, 'protected_attributes']));
  };

  const handleConfirm = async () => {
    if (!problemStatement.trim()) {
      setError('Please provide a problem statement');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const declaration: SuggestedDeclaration = {
        ...formValues,
        journey_id: journeyId,
        problem_statement: problemStatement,
      };

      // Save to API
      const response = await fetch('/api/contextual-validity/declarations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...declaration,
          confirmed_by_user: true,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save declaration');
      }

      onConfirm(declaration);
    } catch (err: unknown) {
      console.error('[ProblemConfirmationModal] Save error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const overallConfidence = getConfidenceLevel(inference.overall_confidence);

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content problem-confirmation-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="confirmation-modal-header">
          <div className="header-title">
            <span className="header-icon">&#129302;</span>
            <h2>Confirm Problem Declaration</h2>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        {/* Overall confidence indicator */}
        <div className="overall-confidence">
          <span className="confidence-label">AI Confidence:</span>
          <span className={`confidence-badge ${overallConfidence.className}`}>
            {Math.round(inference.overall_confidence * 100)}% ({overallConfidence.label})
          </span>
          {inference.requires_user_confirmation && (
            <span className="confirmation-required">Review recommended</span>
          )}
        </div>

        {error && (
          <div className="form-error">
            <span className="error-icon">&#9888;</span>
            {error}
          </div>
        )}

        {/* Main content */}
        <div className="confirmation-body">
          {/* Problem Statement */}
          <div className="confirmation-section">
            <label htmlFor="problem_statement">Problem Statement *</label>
            <textarea
              id="problem_statement"
              value={problemStatement}
              onChange={(e) => setProblemStatement(e.target.value)}
              placeholder="Describe the ML problem you're solving..."
              rows={3}
            />
            <span className="field-hint">Be specific about what you're predicting or classifying</span>
          </div>

          {/* Inferred Problem Type */}
          <div className="confirmation-section">
            <div className="inferred-field">
              <div className="field-header">
                <label>Problem Type</label>
                <ConfidenceIndicator
                  confidence={inference.inferred_problem_type.confidence}
                  edited={editedFields.has('problem_type')}
                />
              </div>
              <select
                value={formValues.problem_type}
                onChange={(e) => handleFieldChange('problem_type', e.target.value)}
              >
                {PROBLEM_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <div className="ai-reasoning">
                <span className="reasoning-icon">&#128161;</span>
                <span>{inference.inferred_problem_type.reasoning}</span>
              </div>
            </div>
          </div>

          {/* Inferred Target Column */}
          {inference.inferred_target_column && (
            <div className="confirmation-section">
              <div className="inferred-field">
                <div className="field-header">
                  <label>Target Column</label>
                  <ConfidenceIndicator
                    confidence={inference.inferred_target_column.confidence}
                    edited={editedFields.has('target_column')}
                  />
                </div>
                <input
                  type="text"
                  value={formValues.target_column || ''}
                  onChange={(e) => handleFieldChange('target_column', e.target.value)}
                  placeholder="Column to predict"
                />
                <div className="ai-reasoning">
                  <span className="reasoning-icon">&#128161;</span>
                  <span>{inference.inferred_target_column.reasoning}</span>
                </div>
              </div>
            </div>
          )}

          {/* Inferred Domain */}
          {inference.inferred_domain && (
            <div className="confirmation-section">
              <div className="inferred-field">
                <div className="field-header">
                  <label>Domain</label>
                  <ConfidenceIndicator
                    confidence={inference.inferred_domain.confidence}
                    edited={editedFields.has('domain')}
                  />
                </div>
                <select
                  value={formValues.domain}
                  onChange={(e) => handleFieldChange('domain', e.target.value)}
                >
                  {DOMAINS.map(domain => (
                    <option key={domain.value} value={domain.value}>
                      {domain.label}
                    </option>
                  ))}
                </select>
                <div className="ai-reasoning">
                  <span className="reasoning-icon">&#128161;</span>
                  <span>{inference.inferred_domain.reasoning}</span>
                </div>
              </div>
            </div>
          )}

          {/* Sensitivity Level */}
          <div className="confirmation-section">
            <div className="inferred-field">
              <div className="field-header">
                <label>Sensitivity Level</label>
              </div>
              <div className="sensitivity-selector">
                {SENSITIVITY_LEVELS.map(level => (
                  <button
                    key={level.value}
                    type="button"
                    className={`sensitivity-option sensitivity-${level.value} ${formValues.sensitivity_level === level.value ? 'selected' : ''}`}
                    onClick={() => handleFieldChange('sensitivity_level', level.value)}
                  >
                    <span className="sensitivity-label">{level.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Protected Attributes */}
          {inference.inferred_protected_attributes.length > 0 && (
            <div className="confirmation-section">
              <div className="inferred-field">
                <div className="field-header">
                  <label>Detected Protected Attributes</label>
                </div>
                <p className="field-explanation">
                  These columns may represent sensitive characteristics. Select which should be monitored for fairness.
                </p>
                <div className="protected-attributes-list">
                  {inference.inferred_protected_attributes.map(attr => {
                    const isSelected = formValues.protected_attributes.includes(attr.column);
                    const attrConfidence = getConfidenceLevel(attr.confidence);

                    return (
                      <div
                        key={attr.column}
                        className={`protected-attr-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleProtectedAttributeToggle(attr.column)}
                      >
                        <div className="attr-header">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Handled by parent onClick
                          />
                          <span className="attr-name">{attr.column}</span>
                          <span className={`attr-type-badge`}>{attr.attribute_type}</span>
                          <span className={`confidence-mini ${attrConfidence.className}`}>
                            {Math.round(attr.confidence * 100)}%
                          </span>
                        </div>
                        <div className="attr-reasoning">{attr.reasoning}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Feature Roles Summary */}
          {inference.feature_roles.length > 0 && (
            <div className="confirmation-section">
              <div className="inferred-field">
                <div className="field-header">
                  <label>Feature Role Summary</label>
                </div>
                <div className="feature-roles-grid">
                  {inference.feature_roles.slice(0, 8).map(fr => (
                    <div key={fr.column} className="feature-role-item">
                      <span className="fr-column">{fr.column}</span>
                      <span className="fr-role">{fr.role}</span>
                    </div>
                  ))}
                  {inference.feature_roles.length > 8 && (
                    <div className="feature-role-more">
                      +{inference.feature_roles.length - 8} more
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="confirmation-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={saving || !problemStatement.trim()}
          >
            {saving ? (
              <>
                <span className="spinner-small"></span>
                Saving...
              </>
            ) : (
              'Confirm & Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENT: Confidence Indicator
// ============================================================================

interface ConfidenceIndicatorProps {
  confidence: number;
  edited: boolean;
}

const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({ confidence, edited }) => {
  const level = getConfidenceLevel(confidence);

  if (edited) {
    return (
      <span className="confidence-indicator edited">
        <span className="edit-icon">&#9998;</span>
        Edited
      </span>
    );
  }

  return (
    <span className={`confidence-indicator ${level.className}`}>
      {Math.round(confidence * 100)}% {level.label}
    </span>
  );
};

export default ProblemConfirmationModal;
